import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { Separator } from '@ordo/ui/separator'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useCreateContact } from 'hooks/api'
import { FetchApiError, getApiErrorMessage } from 'hooks/useFetchApi'
import { Save } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'

import { type ContactFormData, ContactFormFields } from './contact-form-fields'
import { CreateAssociations, type PendingAssociations } from './create-associations'

interface DuplicateContactConflict {
  field: 'email' | 'phoneNumber'
  existingId: string
}

function isDuplicatePayload(
  body: unknown,
): body is { error: 'duplicate'; field: 'email' | 'phoneNumber'; existingId: string } {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    (body as { error: unknown }).error === 'duplicate' &&
    'field' in body &&
    'existingId' in body
  )
}

export function ContactNew() {
  const navigate = useNavigate()
  const { perform: createContact, loading: saving } = useCreateContact()
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    organizationName: '',
    ordoUserId: '',
    timezone: '',
    notes: '',
  })
  const [associations, setAssociations] = useState<PendingAssociations>({
    schoolLocations: [],
    vendorLocations: [],
    organizations: [],
  })
  const [conflict, setConflict] = useState<DuplicateContactConflict | null>(null)

  const handleFieldChange = useCallback((field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const payload: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(formData)) {
        payload[key] = value || undefined
      }
      if (associations.schoolLocations.length > 0) {
        payload.schoolLocations = associations.schoolLocations
      }
      if (associations.vendorLocations.length > 0) {
        payload.vendorLocations = associations.vendorLocations
      }
      if (associations.organizations.length > 0) {
        payload.organizations = associations.organizations
      }
      try {
        const created = await createContact(payload)
        // replace: true drops the create form from history so BackLink's
        // navigate(-1) on the detail page returns to the list, not the form.
        navigate(`/contacts/${created.id}`, { replace: true })
      } catch (err) {
        if (err instanceof FetchApiError && err.status === 409 && isDuplicatePayload(err.body)) {
          setConflict({ field: err.body.field, existingId: err.body.existingId })
          return
        }
        toast.error('Failed to create contact', { description: getApiErrorMessage(err) })
      }
    },
    [formData, associations, createContact, navigate],
  )

  const conflictValue = conflict ? formData[conflict.field] : ''

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/contacts">Back to Contacts</BackLink>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New Contact</CardTitle>
        </CardHeader>

        <Separator />

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit}>
            <ContactFormFields data={formData} onChange={handleFieldChange} />
            <CreateAssociations associations={associations} onChange={setAssociations} />
            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={saving}>
                <Save className="mr-1 h-4 w-4" />
                {saving ? 'Creating...' : 'Create Contact'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Dialog open={!!conflict} onOpenChange={(open) => !open && setConflict(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contact already exists</DialogTitle>
            <DialogDescription>
              A contact with the {conflict?.field === 'phoneNumber' ? 'phone number' : 'email'}{' '}
              <span className="font-medium">{conflictValue}</span> already exists. Open the existing record to update
              it, or change your submission and try again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflict(null)}>
              Edit submission
            </Button>
            <Button
              onClick={() => {
                if (conflict) {
                  navigate(`/contacts/${conflict.existingId}`)
                }
              }}>
              Open existing contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
